import { MailPlus, UserRoundCog } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../components/common/Button';
import { DataTable } from '../../components/common/DataTable';
import { EmptyState } from '../../components/common/EmptyState';
import { FormField } from '../../components/common/FormField';
import { LoadingState } from '../../components/common/LoadingState';
import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { StatusBadge } from '../../components/common/StatusBadge';
import { createInvitation, getInvitations, getMembers, getRoles } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useTenant } from '../../hooks/useTenant';
import { useToast } from '../../contexts/ToastContext';

export function TeamPage() {
  const { user } = useAuth();
  const { activeTenantId } = useTenant();
  const { notify } = useToast();
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({ email: '', roleId: 'instructor' });

  async function refreshTeam() {
    setLoading(true);
    const [nextMembers, nextRoles, nextInvitations] = await Promise.all([getMembers(activeTenantId), getRoles(activeTenantId), getInvitations(activeTenantId)]);
    setMembers(nextMembers);
    setRoles(nextRoles);
    setInvitations(nextInvitations);
    setLoading(false);
  }

  useEffect(() => {
    async function loadTeam() {
      setLoading(true);
      const [nextMembers, nextRoles, nextInvitations] = await Promise.all([getMembers(activeTenantId), getRoles(activeTenantId), getInvitations(activeTenantId)]);
      setMembers(nextMembers);
      setRoles(nextRoles);
      setInvitations(nextInvitations);
      setLoading(false);
    }

    loadTeam().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [activeTenantId]);

  async function onSubmit(event) {
    event.preventDefault();
    setSaving(true);
    try {
      await createInvitation(activeTenantId, user.uid, values);
      notify('Invitation saved. Email delivery is not connected yet.');
      setValues({ email: '', roleId: 'instructor' });
      setOpen(false);
      await refreshTeam();
    } catch (error) {
      console.error(error);
      notify('Unable to save invitation. Try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  const roleName = (roleId) => roles.find((role) => role.id === roleId)?.name || roleId;

  return (
    <div className="page-stack">
      <PageHeader title="Team Members" description="Manage instructors, administrators and staff." action={<Button icon={MailPlus} onClick={() => setOpen(true)}>Invite Member</Button>} />
      {loading ? <LoadingState label="Loading team..." /> : (
        <>
          <DataTable
            rows={members}
            columns={[
              { key: 'displayName', label: 'Name', render: (item) => item.displayName || 'No name' },
              { key: 'email', label: 'Email' },
              { key: 'roleId', label: 'Role', render: (item) => roleName(item.roleId) },
              { key: 'status', label: 'Status', render: (item) => <StatusBadge status={item.status} /> },
              { key: 'action', label: 'Action', render: () => 'Manage' },
            ]}
            empty={<EmptyState title="No team members yet." message="The owner membership appears here after setup." icon={UserRoundCog} />}
          />
          <section className="panel">
            <div className="panel-heading"><h2>Pending Invitations</h2><span>Email delivery comes later via Firebase Functions.</span></div>
            <DataTable
              rows={invitations}
              columns={[
                { key: 'email', label: 'Email' },
                { key: 'roleId', label: 'Role', render: (item) => roleName(item.roleId) },
                { key: 'status', label: 'Status', render: (item) => <StatusBadge status={item.status} /> },
              ]}
              empty={<EmptyState title="No pending invitations." message="Invite records are saved here before email delivery is added." />}
            />
          </section>
        </>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Invite Member" description="Save an invitation record. Email sending is intentionally deferred to backend infrastructure.">
        <form onSubmit={onSubmit} className="modal-form">
          <FormField label="Email" id="invite-email"><input id="invite-email" type="email" required value={values.email} onChange={(event) => setValues({ ...values, email: event.target.value })} /></FormField>
          <FormField label="Role" id="invite-role"><select id="invite-role" value={values.roleId} onChange={(event) => setValues({ ...values, roleId: event.target.value })}>{roles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}</select></FormField>
          <div className="notice-box">This MVP creates the invitation data model only. A trusted backend should send emails later.</div>
          <div className="form-actions"><Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button type="submit" loading={saving}>Save Invitation</Button></div>
        </form>
      </Modal>
    </div>
  );
}
